import NumberFlow from '@number-flow/react'
import { type CSSProperties, type ReactElement, useMemo } from 'react'
import { cx } from 'src/utils/cx'
import dayjs from 'src/utils/dayjs'
import { useCountdownTimer } from 'src/utils/hooks/useCountdownTimer'

export interface CountdownProps {
  targetDate: number | string | Date | dayjs.Dayjs
  /** Whether to treat `targetDate` as a UTC time */
  utc?: boolean
  /**
   * The renderer to render the countdown
   *
   * @default See `renderDefaultCountdown`
   */
  children?: (props: CountdownRenderProps) => ReactElement

  // The following props are for the default countdown renderer

  /**
   * The date format to be used in `dayjs#format`
   *
   * @default "D:HH:mm:ss"
   */
  format?: string
  className?: string
  style?: CSSProperties
}

export interface CountdownRenderProps {
  /** The time left to the target date in milliseconds */
  timeLeft: number
}

/**
 * A countdown
 */
export default function Countdown(props: CountdownProps) {
  const countStart = useMemo(() => {
    const targetMs = (
      props.utc ? dayjs.utc(props.targetDate) : dayjs(props.targetDate)
    ).valueOf()
    const nowMs = (props.utc ? dayjs.utc() : dayjs()).valueOf()
    return targetMs - nowMs
  }, [props.targetDate, props.utc])
  const timeLeft = useCountdownTimer(countStart)

  return props.children
    ? props.children({ timeLeft })
    : renderDefaultCountdown({
        timeLeft,
        format: props.format,
        className: props.className,
        style: props.style,
      })
}

/**
 * The default countdown renderer
 */
function renderDefaultCountdown(
  props: StyleProps & CountdownRenderProps & { format?: string },
): ReactElement {
  return (
    <span className={props.className} style={props.style}>
      {dayjs
        .duration(props.timeLeft, 'ms')
        .format(props.format || 'D:HH:mm:ss')}
    </span>
  )
}

// An example of a countdown timer.
// Used to be used in the Xmas event.
function CountdownTimerExample(props: StyleProps) {
  const endDate = dayjs('2099-12-31 23:59:59')

  return (
    <Countdown targetDate={endDate} utc>
      {({ timeLeft }) => (
        <span
          className={cx(
            'inline-grid grid-flow-col items-center px-1 tabular-nums text-white bg-black rounded',
            props.className,
          )}
          style={props.style}
        >
          {dayjs
            .duration(timeLeft)
            .format('D:H:m:s')
            .split(':')
            .map(Number)
            .map((num, idx) => (
              <>
                {idx > 0 && ':'}
                <NumberFlow
                  key={idx}
                  value={num}
                  trend={-1}
                  digits={idx === 0 ? undefined : { 1: { max: 5 } }}
                  format={idx === 0 ? undefined : { minimumIntegerDigits: 2 }}
                />
              </>
            ))}
        </span>
      )}
    </Countdown>
  )
}
